import { useMemo } from 'react';
import { Loader2, AlertCircle, History } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useCableHistory } from '../hooks/useCable';
import type { CableSpool } from '@/api/legacy';

interface CableHistorySheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  spool: CableSpool | null;
}

export function CableHistorySheet({
  open,
  onOpenChange,
  spool,
}: CableHistorySheetProps) {
  const { data: history, isLoading, error } = useCableHistory(
    spool ? parseInt(spool.id) : null
  );

  const totalMeters = useMemo(() => {
    if (!history) return 0;
    return history.reduce((sum, item) => sum + (parseInt(item.rashod) || 0), 0);
  }, [history]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="max-h-[80vh]">
        <SheetHeader>
          <SheetTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <History className="h-5 w-5" />
              История бухты #{spool?.name}
            </span>
            {totalMeters > 0 && (
              <span className="text-sm font-normal text-muted-foreground">
                Всего: {totalMeters} м
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 overflow-y-auto max-h-[60vh] -mx-4 px-4">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <AlertCircle className="h-8 w-8 text-destructive mb-2" />
              <p className="text-sm text-muted-foreground">
                {error instanceof Error ? error.message : 'Ошибка загрузки'}
              </p>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && history?.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <History className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                История расхода пуста
              </p>
            </div>
          )}

          {/* History list */}
          {!isLoading && !error && history && history.length > 0 && (
            <div className="space-y-3">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="p-3 rounded-lg border bg-card"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {item.company || 'Клиент не указан'}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(item.created)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{item.rashod}м</div>
                      <div className="text-xs text-muted-foreground">
                        {item.username}
                      </div>
                    </div>
                  </div>
                  {item.paid === 0 && (
                    <div className="mt-2 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-1 rounded inline-block">
                      Не оплачено
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function formatDate(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}
